import type { IconType } from 'react-icons';
import {
  BiMinusCircle,
  BiSolidCheckCircle,
  BiSolidErrorCircle,
  BiSolidInfoCircle,
  BiSolidXCircle,
} from 'react-icons/bi';
import './StatusChip.css';

export type ChipTone = 'success' | 'danger' | 'warning' | 'info' | 'neutral';

interface StatusChipProps {
  label: string;
  tone: ChipTone;
}

/** Decorative leading icon per tone — §4.6 "optional leading icon" (icon swap per
 * docs/design/12-reskin-spec.md §3). The text label (required, always passed by callers) remains
 * the actual status signal; this is a scan aid only, so it's aria-hidden and never the sole way
 * the status is conveyed (§1.3 color/icon independence). */
const ICON_BY_TONE: Record<ChipTone, IconType> = {
  success: BiSolidCheckCircle,
  danger: BiSolidXCircle,
  warning: BiSolidErrorCircle,
  info: BiSolidInfoCircle,
  neutral: BiMinusCircle,
};

/** 10-design-system.md §4.6 — pill chip, colored background + darker matching text (never
 * white-on-saturated, for AA contrast at small sizes). Color is never the only signal: callers
 * should pass a label that already states the status in words (never an icon/color-only chip). */
export function StatusChip({ label, tone }: StatusChipProps) {
  const Icon = ICON_BY_TONE[tone];
  return (
    <span className={`fa-chip fa-chip--${tone}`}>
      <span className="fa-chip__icon" aria-hidden="true">
        <Icon />
      </span>
      {label}
    </span>
  );
}
